import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { ExperienceListFilters } from 'actions/ExperienceActions'
import LoyaltyLoungePassModal from './LoyaltyLoungePassModal'
import getLoyaltyLoungePassItemViews from 'checkout/selectors/view/itemViews/getLoyaltyLoungePassItemViews'
import LoyaltyBenefitsLoungeItem from './LoyaltyBenefitsLoungeItem'
import LoyaltyBenefitCompactRow from './LoyaltyBenefitCompactRow'
import getLoyaltyOrderViewLoungePassFilters from 'selectors/orderView/getLoyaltyOrderViewLoungePassFilters'
import useOrder from 'hooks/Order/useOrder'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import { isInfiniteLoungePassRedemptionCountKnown } from 'loyalty/lib/infiniteFreeLoungePassLimit'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { nonNullable, unique } from 'lib/array/arrayUtils'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { fetchOrder } from 'actions/OrderActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import getOrderAccommodationStartDate from 'lib/order/getOrderAccommodationStartDate'
import getOrderAccommodationEndDate from 'lib/order/getOrderAccommodationEndDate'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

interface Props {
  order: App.Order
  layout?: 'tile' | 'row'
  /** Per-order lounge availability filters; falls back to the route-viewed order's filters. */
  availabilityFilters?: Array<ExperienceListFilters>
}

function LoyaltyOrderViewBenefitsLoungeItem({ order, layout = 'tile', availabilityFilters: availabilityFiltersProp }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const views = useAppSelector(state => getLoyaltyLoungePassItemViews(state).data)
  const cartOrderId = useAppSelector(state => state.checkout.cart.existingOrder?.id)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const showModal = useContext(ModalContext)
  // Only Infinite members need redeemed passes. Other tiers use their account allowance.
  const [storedOrder, fetchingOrder, orderError] = useOrder(isInfiniteTier ? order.id : undefined)
  const fullOrder = storedOrder?.type === 'full' ? storedOrder : order.type === 'full' ? order : undefined
  const redemptionCountKnown = isInfiniteLoungePassRedemptionCountKnown(isInfiniteTier, fullOrder)
  const loyaltyOrder = isInfiniteTier ? (redemptionCountKnown ? fullOrder : undefined) : order
  const loyaltyDetailsFailed = isInfiniteTier && (
    (!!fullOrder && fullOrder.loyaltyDetails.benefitsLoaded === false)
    || (!fullOrder && !!orderError)
  )
  const loungePassesUnavailable = isInfiniteTier && !loyaltyOrder
  const loungePassLoadErrorMessage = intl.formatMessage({
    id: 'aa1V05XDnE6pDhjDY0dB',
    defaultMessage: 'We couldn\'t load the lounge passes for this booking. Try again.',
    description: 'Error shown on a booking when redeemed Infinite lounge passes failed to load',
  })
  const tryAgainLabel = intl.formatMessage({
    id: 'VVme-v25JTxeioUuXQcn',
    defaultMessage: 'Try again',
    description: 'Button label to retry loading redeemed Infinite lounge passes on a booking',
  })
  const retryLoungePasses = useCallback(() => {
    dispatch(fetchOrder(order.id, { force: true }))
  }, [dispatch, order.id])
  const retryLoungePassesAction = useMemo(() => ({
    children: tryAgainLabel,
    disabled: !!fetchingOrder,
    onClick: retryLoungePasses,
  }), [fetchingOrder, retryLoungePasses, tryAgainLabel])

  const viewedOrderFilters = useAppSelector(state => getLoyaltyOrderViewLoungePassFilters(state))
  const availabilityFilters = availabilityFiltersProp ?? viewedOrderFilters

  const orderLocationsCountryCodes = useMemo(() => {
    return nonNullable(unique(order.items.map(item => item.offer.property?.geoData?.countryCode)))
  }, [order])

  const onAddItems = useCallback((items: Array<App.Checkout.LoyaltyLoungePassItem>) => {
    if (!loyaltyOrder) return
    dispatch(addToCheckoutCart(items, {
      regionCode: loyaltyOrder.regionCode,
      currencyCode: loyaltyOrder.currencyCode,
      order: loyaltyOrder,
      mode: 'loyalty-redeem-benefits',
    }))
    dispatch(navigateToCheckout())
  }, [dispatch, loyaltyOrder])

  const checkInDate = getOrderAccommodationStartDate(order)
  const checkOutDate = getOrderAccommodationEndDate(order)
  const flightDates = useMemo(() => order.flightItems?.map(item => ({
    departingAirport: item.departingAirport,
    arrivalAirport: item.arrivalAirport,
    departureDate: item.departureDate,
    arrivalDate: item.arrivalDate,
  })), [order.flightItems])

  const onRedeem = useCallback(() => {
    if (loungePassesUnavailable || !loyaltyOrder) return
    showModal(<LoyaltyLoungePassModal
      availabilityFilters={availabilityFilters}
      locationsCountryCodes={orderLocationsCountryCodes}
      onAddItems={onAddItems}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      flightDates={flightDates}
      bookingOrder={loyaltyOrder}
    />)
  }, [showModal, availabilityFilters, orderLocationsCountryCodes, onAddItems, checkInDate, checkOutDate, flightDates, loyaltyOrder, loungePassesUnavailable])

  const onEdit = useCallback(() => {
    if (loungePassesUnavailable || !loyaltyOrder) return
    showModal(<LoyaltyLoungePassModal
      defaultStep="tickets"
      loungePassViews={views}
      availabilityFilters={availabilityFilters}
      locationsCountryCodes={orderLocationsCountryCodes}
      onAddItems={onAddItems}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      flightDates={flightDates}
      bookingOrder={loyaltyOrder}
    />)
  }, [showModal, views, availabilityFilters, orderLocationsCountryCodes, onAddItems, checkInDate, checkOutDate, flightDates, loyaltyOrder, loungePassesUnavailable])

  const loadErrorBanner = loyaltyDetailsFailed ? <MessageBanner
    kind="critical"
    icon={<LineInfoCircleIcon />}
    primaryActionButtonProps={retryLoungePassesAction}
  >
    {loungePassLoadErrorMessage}
  </MessageBanner> : null

  if (layout === 'row') {
    // Only treat cart lounge passes as "added" when the cart belongs to this order.
    const hasAddedForThisOrder = cartOrderId === order.id && views.length > 0
    const editAction = <TextButton kind="secondary" variant="default" size="small" shape="round" startIcon={<LineEditAltIcon />} onClick={onEdit} disabled={!loyaltyOrder}>
      {intl.formatMessage({ id: 'cp4eOp-RVFNlSi_b_ff5', defaultMessage: 'Edit', description: 'Action button label to edit lounge pass selection in the loyalty program section' })}
    </TextButton>
    const addAction = <TextButton kind="secondary" variant="default" size="small" shape="round" startIcon={<LinePlusIcon />} onClick={onRedeem} disabled={!loyaltyOrder}>
      <CSSBreakpoint as="span" max="mobile">
        {intl.formatMessage({ id: 'tSyiNkLzkGgaIKhSivRH', defaultMessage: 'Add', description: 'Short add-to-booking button label shown on mobile devices' })}
      </CSSBreakpoint>
      <CSSBreakpoint as="span" min="tablet">
        {intl.formatMessage({ id: 'iZfWZcvSob6VJ3UC00LW', defaultMessage: 'Add to booking', description: 'Action button label to add a loyalty benefit to an upcoming booking' })}
      </CSSBreakpoint>
    </TextButton>
    return <>
      {loadErrorBanner}
      <LoyaltyBenefitCompactRow
        benefitType="lounge_pass"
        heading={intl.formatMessage({
          id: 'zSPXAvw6egjQ1SuGwY-r',
          defaultMessage: 'Free airport lounge passes',
          description: 'Heading for lounge pass benefit tile in the loyalty benefits section',
        })}
        action={hasAddedForThisOrder ? editAction : addAction}
      />
    </>
  }

  return <>
    {loadErrorBanner}
    <LoyaltyBenefitsLoungeItem
      views={views}
      onEdit={onEdit}
      onRedeem={onRedeem}
      bookingOrder={loyaltyOrder}
      redemptionCountKnown={redemptionCountKnown}
      actionsDisabled={!loyaltyOrder}
    />
  </>
}

export default LoyaltyOrderViewBenefitsLoungeItem
